import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Icon,
  Popover,
  PopoverAnchor,
  PopoverContent,
  cn,
} from "@nexus/ui";

export type AnalyzeBlocker = {
  id: string;
  label: string;
};

export interface AnalyzeBlockersButtonProps {
  canAnalyze: boolean;
  isSubmitting: boolean;
  blockers: AnalyzeBlocker[];
  onAnalyze: () => void;
}

/**
 * Primary Analyze CTA. When preconditions are unmet, clicking opens an animated
 * popover above the button listing what's missing — no scattered inline alerts.
 */
export function AnalyzeBlockersButton({
  canAnalyze,
  isSubmitting,
  blockers,
  onAnalyze,
}: AnalyzeBlockersButtonProps) {
  const { t } = useTranslation();
  const [blockersOpen, setBlockersOpen] = useState(false);
  const isBlocked = !canAnalyze && !isSubmitting;

  useEffect(() => {
    if (canAnalyze) setBlockersOpen(false);
  }, [canAnalyze]);

  function handleClick() {
    if (isSubmitting) return;
    if (!canAnalyze) {
      setBlockersOpen(true);
      return;
    }
    setBlockersOpen(false);
    onAnalyze();
  }

  return (
    <Popover
      open={blockersOpen && isBlocked}
      onOpenChange={(open) => {
        if (isBlocked) setBlockersOpen(open);
      }}
    >
      <PopoverAnchor asChild>
        <Button
          type="button"
          iconStart="sparkles"
          onClick={handleClick}
          aria-disabled={isBlocked || undefined}
          aria-expanded={blockersOpen && isBlocked}
          aria-haspopup={isBlocked ? "dialog" : undefined}
          className={cn(
            "w-full shrink-0 sm:w-auto",
            isBlocked && "opacity-60",
          )}
        >
          {isSubmitting
            ? t("preflight.inputCard.analyzing")
            : t("preflight.inputCard.analyze")}
        </Button>
      </PopoverAnchor>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className="border-error/20 bg-card w-72 p-0 shadow-lg"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col gap-2 p-3"
        >
          <p className="text-foreground flex items-center gap-1.5 text-xs font-medium">
            <Icon name="alert-circle" size="xs" className="text-error shrink-0" />
            {t("preflight.inputCard.blockersTitle")}
          </p>
          <ul className="text-muted-foreground list-disc space-y-1 pl-4 text-xs">
            {blockers.map((blocker) => (
              <li key={blocker.id}>{blocker.label}</li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}
